import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { createId } from "@/lib/ids";

export interface PencilPoint {
  x: number;
  y: number;
}

export interface PencilVectorizeOptions {
  tolerance?: number;
  idPrefix?: string;
}

export interface PencilVectorizeSession {
  rawPointCount: number;
  cleanPoints: PencilPoint[];
  acceptedPoints: PencilPoint[];
  candidate?: PencilPoint;
  toleranceSquared: number;
  maxSegmentLengthSquared: number;
  networkCache?: {
    idPrefix: string;
    simplified: PencilPoint[];
    network: VectorNetwork;
  };
}

export function vectorizePencilStroke(points: PencilPoint[], options: PencilVectorizeOptions = {}): VectorNetwork | undefined {
  const session = createPencilVectorizeSession(options);
  appendPencilPointsToSession(session, points);
  return pencilVectorizeSessionToNetwork(session, options);
}

export function createPencilVectorizeSession(options: PencilVectorizeOptions = {}): PencilVectorizeSession {
  const tolerance = options.tolerance ?? 2;
  const maxSegmentLength = Math.max(12, tolerance * 8);
  return {
    rawPointCount: 0,
    cleanPoints: [],
    acceptedPoints: [],
    toleranceSquared: tolerance * tolerance,
    maxSegmentLengthSquared: maxSegmentLength * maxSegmentLength,
  };
}

export function appendPencilPointsToSession(session: PencilVectorizeSession, points: PencilPoint[]): void {
  for (let index = session.rawPointCount; index < points.length; index += 1) {
    appendPencilPointToSession(session, points[index]!);
  }
  session.rawPointCount = points.length;
}

export function pencilVectorizeSessionPoints(session: PencilVectorizeSession): PencilPoint[] {
  if (session.cleanPoints.length < 2) {
    return [...session.cleanPoints];
  }

  const last = session.cleanPoints.at(-1)!;
  const points = [...session.acceptedPoints];
  const previous = points.at(-1);
  if (!previous || squaredDistance(previous, last) > 0.000001) {
    points.push(last);
  }

  return points;
}

export function pencilVectorizeSessionToNetwork(session: PencilVectorizeSession, options: PencilVectorizeOptions = {}): VectorNetwork | undefined {
  const simplified = pencilVectorizeSessionPoints(session);
  if (simplified.length < 2) {
    return undefined;
  }

  if (options.idPrefix) {
    return cachedPencilNetwork(session, simplified, options.idPrefix);
  }

  return pencilPointsToNetwork(simplified, options);
}

function cachedPencilNetwork(session: PencilVectorizeSession, simplified: PencilPoint[], idPrefix: string): VectorNetwork {
  const cache = session.networkCache?.idPrefix === idPrefix ? session.networkCache : undefined;
  if (!cache) {
    const network = pencilPointsToNetwork(simplified, { idPrefix });
    session.networkCache = { idPrefix, simplified, network };
    return snapshotNetwork(network);
  }

  const previous = cache.simplified;
  const stableCount = commonStablePrefixLength(previous, simplified);
  const rebuildVertexStart = Math.max(0, stableCount - 3);
  const rebuildEdgeStart = Math.max(0, rebuildVertexStart - 1);
  cache.network.vertices.length = rebuildVertexStart;
  cache.network.edges.length = rebuildEdgeStart;

  for (let index = rebuildVertexStart; index < simplified.length; index += 1) {
    const point = simplified[index]!;
    cache.network.vertices[index] = {
      id: `${idPrefix}_vertex_${index}`,
      x: point.x,
      y: point.y,
    };
  }

  for (let index = rebuildEdgeStart; index < simplified.length - 1; index += 1) {
    cache.network.edges[index] = pencilEdgeForPoints(simplified, idPrefix, index);
  }

  cache.simplified = simplified;
  // The internal cache network is mutated in place to reuse vertex/edge objects,
  // but geometry caches (e.g. the mesh WeakMap and geometry pool) key tessellation
  // by network object identity and assume immutability. Hand out a fresh wrapper so
  // the growing draft stroke re-tessellates instead of reusing a stale mesh.
  return snapshotNetwork(cache.network);
}

function snapshotNetwork(network: VectorNetwork): VectorNetwork {
  return {
    ...network,
    vertices: [...network.vertices],
    edges: [...network.edges],
  };
}

function commonStablePrefixLength(previous: PencilPoint[], next: PencilPoint[]): number {
  if (previous.length === 0 || next.length === 0) {
    return 0;
  }

  const firstPrevious = previous[0]!;
  const firstNext = next[0]!;
  if (firstPrevious.x !== firstNext.x || firstPrevious.y !== firstNext.y) {
    return 0;
  }

  return Math.max(0, Math.min(previous.length, next.length) - 3);
}

function pencilPointsToNetwork(simplified: PencilPoint[], options: PencilVectorizeOptions): VectorNetwork {
  const vertices = simplified.map((point, index) => ({
    id: options.idPrefix ? `${options.idPrefix}_vertex_${index}` : createId("vertex"),
    x: point.x,
    y: point.y,
  }));
  const edges = vertices.slice(0, -1).map((vertex, index) => pencilEdgeForVertices(simplified, options.idPrefix, index, vertex.id, vertices[index + 1]!.id));

  return {
    vertices,
    edges,
    regions: [],
    fillRule: "nonzero",
  };
}

function pencilEdgeForPoints(simplified: PencilPoint[], idPrefix: string | undefined, index: number) {
  return pencilEdgeForVertices(simplified, idPrefix, index, `${idPrefix}_vertex_${index}`, `${idPrefix}_vertex_${index + 1}`);
}

function pencilEdgeForVertices(simplified: PencilPoint[], idPrefix: string | undefined, index: number, fromVertexId: string, toVertexId: string) {
  const previousPoint = simplified[Math.max(0, index - 1)]!;
  const currentPoint = simplified[index]!;
  const nextPoint = simplified[index + 1]!;
  const afterNextPoint = simplified[Math.min(simplified.length - 1, index + 2)]!;

  return {
    id: idPrefix ? `${idPrefix}_edge_${index}` : createId("edge"),
    from: {
      vertexId: fromVertexId,
      handle: {
        x: currentPoint.x + (nextPoint.x - previousPoint.x) / 6,
        y: currentPoint.y + (nextPoint.y - previousPoint.y) / 6,
      },
    },
    to: {
      vertexId: toVertexId,
      handle: {
        x: nextPoint.x - (afterNextPoint.x - currentPoint.x) / 6,
        y: nextPoint.y - (afterNextPoint.y - currentPoint.y) / 6,
      },
    },
    directed: false,
  };
}

function appendPencilPointToSession(session: PencilVectorizeSession, point: PencilPoint): void {
  const previousClean = session.cleanPoints.at(-1);
  if (previousClean && Math.hypot(point.x - previousClean.x, point.y - previousClean.y) < 1) {
    return;
  }

  session.cleanPoints.push(point);
  if (session.cleanPoints.length === 1) {
    session.acceptedPoints.push(point);
    return;
  }

  if (session.cleanPoints.length === 2) {
    session.candidate = point;
    return;
  }

  const current = point;
  const anchor = session.acceptedPoints.at(-1)!;
  const candidate = session.candidate;
  if (candidate) {
    const candidateDistanceSquared = pointSegmentDistanceSquared(candidate, anchor, current);
    const currentDistanceSquared = squaredDistance(anchor, current);
    if (candidateDistanceSquared >= session.toleranceSquared || currentDistanceSquared >= session.maxSegmentLengthSquared) {
      session.acceptedPoints.push(candidate);
    }
  }
  session.candidate = current;
}

function pointSegmentDistanceSquared(point: PencilPoint, from: PencilPoint, to: PencilPoint): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= 0.000001) {
    return squaredDistance(point, from);
  }

  const t = Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));
  return squaredDistance(point, {
    x: from.x + dx * t,
    y: from.y + dy * t,
  });
}

function squaredDistance(left: PencilPoint, right: PencilPoint): number {
  const dx = left.x - right.x;
  const dy = left.y - right.y;
  return dx * dx + dy * dy;
}
